import { inject, Injector, Service, signal, type Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CurrentAccountService } from '@aurora/modules/authentication';
import { TranslocoService } from '@jsverse/transloco';
import { Apollo } from 'apollo-angular';
import {
  catchError,
  combineLatest,
  EMPTY,
  map,
  of,
  switchMap,
  type Observable,
  type Subscription,
} from 'rxjs';
import { TOUR_SCREEN, type Tour } from './manual-tour';
import {
  manualChapterQuery,
  manualIndexQuery,
  type ManualChapter,
  type ManualIndexChapter,
  type ManualTour,
} from './manual.graphql';

const toTour = (tour: ManualTour): Tour => ({
  id: tour.id,
  title: tour.title,
  screen: Object.values(TOUR_SCREEN).find((screen) => screen === tour.screen) ?? null,
  steps: tour.steps.map(({ target, title, text }) => ({ target, title, text })),
});

/**
 * Data access for the in-app manual. The backend filters both operations by the caller's
 * permissions, so whatever this service holds is already readable by the current user.
 */
@Service()
export class ManualService {
  private readonly apollo = inject(Apollo);
  private readonly transloco = inject(TranslocoService);
  private readonly currentAccount = inject(CurrentAccountService);
  private readonly injector = inject(Injector);
  private readonly _index = signal<ManualIndexChapter[]>([]);
  private readonly _indexLoaded = signal(false);
  private subscription: Subscription | null = null;

  /** Chapters of the manual in the active language, in reading order (general ones first). */
  readonly index: Signal<ManualIndexChapter[]> = this._index.asReadonly();

  /** Whether the index response has arrived at least once (an empty or failed one counts); false while no account is present. */
  readonly indexLoaded: Signal<boolean> = this._indexLoaded.asReadonly();

  /**
   * Keeps the index in sync with the session: loads it for the current account and language, and
   * reloads it whenever the account id changes (sign-in, impersonation start or end) or the
   * language changes. While no account is present the index is cleared, `indexLoaded` goes back
   * to false and no query is sent. A failed request leaves the previous index in place (the
   * global GraphQL error link already told the user) and the service keeps listening. Registered
   * with `provideAuthenticatedInitializer`, never called from a constructor. Calling it again
   * replaces the previous subscription.
   */
  start(): void {
    this.subscription?.unsubscribe();
    let lastAccountId: string | null = null;
    this.subscription = combineLatest([
      toObservable(this.currentAccount.accountId, { injector: this.injector }),
      this.transloco.langChanges$,
    ])
      .pipe(
        switchMap(([accountId, lang]) => {
          if (accountId === null) {
            lastAccountId = null;
            this._index.set([]);
            this._indexLoaded.set(false);
            return EMPTY;
          }
          // Another principal's chapters must never outlive a failed reload for this one.
          if (accountId !== lastAccountId) this._index.set([]);
          lastAccountId = accountId;

          return this.apollo.query({ query: manualIndexQuery, variables: { lang } }).pipe(
            map((result) => (result.error ? null : (result.data?.manualIndex ?? null))),
            // The error link already reported the failure; completing the inner stream here only
            // keeps the outer one alive for the next account or language change.
            catchError(() => of(null)),
          );
        }),
      )
      .subscribe((index) => {
        if (index) this._index.set(index);
        this._indexLoaded.set(true);
      });
  }

  /**
   * Id of the chapter that documents the screen at `url`, or `null` when none does (or the index
   * has not loaded, or the user may not read it). A chapter matches when its route equals the URL
   * path or the URL continues it with `/`; the longest route wins, so `/iam/account/edit/<id>`
   * resolves to the `/iam/account` chapter and `/iam/accounting` does not. Query string and
   * fragment are ignored. Reads the `index` signal, so it is reactive inside `computed()` and
   * templates.
   *
   * @param url Router URL of the screen, e.g. `/iam/users/edit/42?tab=2`.
   */
  chapterForRoute(url: string): string | null {
    const path = url.split(/[?#]/)[0];
    let best: ManualIndexChapter | null = null;
    for (const chapter of this._index()) {
      const route = chapter.route;
      if (!route || !(path === route || path.startsWith(`${route}/`))) continue;
      if (!best || route.length > (best.route?.length ?? 0)) best = chapter;
    }
    return best?.id ?? null;
  }

  /**
   * Guided tours that may apply to the screen at `route`: every general tour (they run on any
   * screen of their kind; the runner keeps the ones whose anchor is on screen), then the own tour
   * of the chapter that documents the screen. Empty before the index has loaded. Reactive like
   * `chapterForRoute`. The GraphQL `__typename`s are dropped, so the runner gets plain `Tour`s.
   */
  toursForRoute(route: string): Tour[] {
    const index = this._index();
    const general = index
      .filter((chapter) => chapter.route === null)
      .map((chapter) => chapter.tour);
    const own = index.find((chapter) => chapter.route === route)?.tour;
    return [...general, own].filter((tour) => tour != null).map(toTour);
  }

  /**
   * Tour with that id from any chapter of the index (the assistant's guided action declares a tour
   * by id, not by route); `null` when none has it or the index has not loaded. Reactive.
   */
  tourById(id: string): Tour | null {
    const tour = this._index().find((chapter) => chapter.tour?.id === id)?.tour;
    return tour ? toTour(tour) : null;
  }

  /**
   * One chapter in the active language. Emits `null` when the backend refuses it or does not
   * know it (both look the same by design), or when the request fails.
   */
  chapter(id: string): Observable<ManualChapter | null> {
    return this.apollo
      .query({
        query: manualChapterQuery,
        variables: { lang: this.transloco.getActiveLang(), id },
      })
      .pipe(
        map((result) => (result.error ? null : (result.data?.manualChapter ?? null))),
        catchError(() => of(null)),
      );
  }
}
